import React, { useEffect, useMemo, useState } from 'react';
import { DataTable, TagColored, Text } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import {
  DEFAULT_COST_CONFIG,
  LENGTH_TO_TOKENS,
  fetchInfrastructureComparison,
} from './data/performanceMetricsApi';
import {
  calculateThroughputRps,
  calculateCostPer1MTokensClassic,
  calculateCostPer1MTokensGenerative,
} from './data/derivedMetrics';
import { rankColors, DEFAULT_COLOR } from './data/rankColors';

/**
 * Infrastructure Comparison table — one row per environment the user
 * configured in the Performance Filters modal, sourced from the
 * `performance_metrics` / `machine_costs` collections via the backend.
 *
 * Each numeric column is rendered as an AntD tag whose color is decided
 * by `rankColors` (best = green, worst = red, ties share a color, single
 * unique value = no color). Throughput is derived client-side from the
 * raw latency. Cost per 1M Tokens is derived client-side for student
 * rows from `$/hour ÷ throughput`; teacher rows instead carry an
 * explicit `costPer1MTokensUsd` seeded on the doc (managed-service
 * per-token billing — `$/hour ÷ throughput` doesn't apply).
 *
 * @param {Object} props
 * @param {string} props.baseModelName
 * @param {import('./data/performanceMetricsApi').PerformanceFilters} props.filters
 * @param {import('./data/performanceMetricsApi').CostConfig} [props.costConfig]
 * @param {string} [props.currentModelLabel]
 * @param {string|null} [props.comparisonBaseModelName] - Base model of the
 *        "Compare:" selection. When the selection is the usecase teacher
 *        (`comparisonModelKind === 'teacher'`), the backend appends an
 *        extra row for the teacher's latency at the same point.
 * @param {('student'|'teacher')} [props.comparisonModelKind]
 * @param {string|null} [props.comparisonModelLabel] - Display label used
 *        in the appended teacher row's "Configuration" column.
 * @param {string} [props.taskType] - One of TaskType values; controls
 *        whether the derived Throughput / Cost-per-1M-tokens columns are shown.
 */
export default function InfrastructureComparisonTable({
  baseModelName,
  filters,
  costConfig = DEFAULT_COST_CONFIG,
  currentModelLabel = 'Specific AI V1',
  comparisonBaseModelName = null,
  comparisonModelKind = 'student',
  comparisonModelLabel = null,
  taskType = null,
}) {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const concurrency = filters?.concurrency ?? 1;
  // Cost-per-1M-tokens math needs a representative input-token count.
  // After the switch from a free-form token input to a Short/Long Select,
  // we map the chosen length category to a fixed canonical count.
  const averageInputContextLength =
    LENGTH_TO_TOKENS[filters?.lengthCategory] ?? LENGTH_TO_TOKENS.short;
  const isClassicTask =
    taskType === TaskType.NER ||
    taskType === TaskType.NER_WITH_RELATIONS ||
    taskType === TaskType.classification;
  const costPeriod = costConfig?.period || 'monthly';
  const estimatedCostColumnTitle =
    costPeriod === 'hourly'
      ? 'Estimated Cost ($/hour)'
      : costPeriod === 'daily'
        ? 'Estimated Cost ($/day)'
        : 'Estimated Cost ($/month)';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchInfrastructureComparison({
      baseModelName,
      filters,
      costConfig,
      currentModelLabel,
      comparisonBaseModelName,
      comparisonModelKind,
      comparisonModelLabel,
    })
      .then((data) => {
        if (!cancelled) setRawRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRawRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    baseModelName,
    filters,
    costConfig,
    currentModelLabel,
    comparisonBaseModelName,
    comparisonModelKind,
    comparisonModelLabel,
  ]);

  // Concurrency stamped on each row by the backend, but fall back to the
  // active filter value so the derived metrics still work if the field
  // is missing.
  const rowsWithDerived = useMemo(
    () =>
      rawRows.map((row) => {
        const concurrencyLevel = row.concurrencyLevel ?? concurrency;
        const throughputRps = calculateThroughputRps({
          avgLatencyMs: row.avgLatencyMs,
          concurrencyLevel,
        });
        // Teacher rows carry an explicit per-token price seeded from the
        // workbook (e.g. $3 / 1M tokens for managed-service models like
        // Claude). Student rows have `costPer1MTokensUsd: null` and fall
        // through to the derived `$/hour ÷ throughput` calculation.
        const costPer1MTokens =
          row.costPer1MTokensUsd != null
            ? row.costPer1MTokensUsd
            : isClassicTask
              ? calculateCostPer1MTokensClassic({
                  costPerHourUsd: row.costPerHourUsd,
                  throughputRps,
                  avgInputTokens: averageInputContextLength,
                })
              : calculateCostPer1MTokensGenerative({
                  costPerHourUsd: row.costPerHourUsd,
                  throughputTokensPerSec: throughputRps,
                });
        const estimatedCost = row.estimatedCost ?? row.estimatedCostMonthly ?? null;
        return { ...row, throughputRps, costPer1MTokens, estimatedCost };
      }),
    [rawRows, concurrency, isClassicTask, averageInputContextLength]
  );

  // Rank colors per column. Each entry is a Map<value, color> so we can
  // look up by the row's value (stable across user-triggered re-sorts of
  // the table — index-based lookups would shift).
  const colorMaps = useMemo(
    () => ({
      avg: rankColors(rowsWithDerived.map((r) => r.avgLatencyMs), {
        lowerIsBetter: true,
      }),
      p95: rankColors(rowsWithDerived.map((r) => r.p95LatencyMs), {
        lowerIsBetter: true,
      }),
      throughput: rankColors(rowsWithDerived.map((r) => r.throughputRps), {
        lowerIsBetter: false,
      }),
      costPer1M: rankColors(rowsWithDerived.map((r) => r.costPer1MTokens), {
        lowerIsBetter: true,
      }),
      costEstimated: rankColors(
        rowsWithDerived.map((r) => r.estimatedCost?.value ?? null),
        { lowerIsBetter: true }
      ),
    }),
    [rowsWithDerived]
  );

  // Show derived columns when concurrency > 1, OR for any task type that
  // is NOT NER / NER_WITH_RELATIONS / classification (i.e. summarization
  // and friends), where per-token throughput/cost is meaningful even at
  // serial concurrency.
  const showDerivedColumns = useMemo(
    () =>
      (concurrency ?? 1) > 1 ||
      (taskType != null &&
        taskType !== TaskType.NER &&
        taskType !== TaskType.NER_WITH_RELATIONS &&
        taskType !== TaskType.classification),
    [concurrency, taskType]
  );

  const columns = useMemo(() => {
    // Look up the color for the row's value in this column's color map.
    // Using the value (not the row index) keeps colors stable when AntD
    // re-orders rows after the user clicks a column sort header.
    const renderTag = (value, colorMap, formatted) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return <Text variant="base-regular" color="secondary">-</Text>;
      }
      const tagColor = colorMap.get(value) ?? DEFAULT_COLOR;
      return <TagColored label={formatted} style={{ backgroundColor: tagColor, color: '#fff' }} />;
    };

    const cols = [
      {
        title: 'Configuration',
        dataIndex: 'configurationLabel',
        key: 'configurationLabel',
        render: (_, row) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text variant="base-bold">{row.modelLabel}</Text>
            <Text variant="small-regular" color="secondary">
              {row.configurationLabel}
            </Text>
          </div>
        ),
      },
      {
        title: 'Avg Latency (ms)',
        dataIndex: 'avgLatencyMs',
        key: 'avgLatencyMs',
        sorter: (a, b) => (a?.avgLatencyMs ?? 0) - (b?.avgLatencyMs ?? 0),
        render: (value) => renderTag(value, colorMaps.avg, value?.toFixed(1)),
      },
      {
        title: 'P95 Latency (ms)',
        dataIndex: 'p95LatencyMs',
        key: 'p95LatencyMs',
        sorter: (a, b) => (a?.p95LatencyMs ?? 0) - (b?.p95LatencyMs ?? 0),
        render: (value) => renderTag(value, colorMaps.p95, value?.toFixed(1)),
      },
    ];

    if (showDerivedColumns) {
      cols.push({
        title: isClassicTask
          ? 'Throughput (Requests/Sec)'
          : 'Throughput (Tokens/Sec)',
        dataIndex: 'throughputRps',
        key: 'throughputRps',
        sorter: (a, b) => (a?.throughputRps ?? 0) - (b?.throughputRps ?? 0),
        render: (value) => renderTag(value, colorMaps.throughput, value?.toFixed(2)),
      });
      cols.push({
        title: 'Cost per 1M Tokens ($)',
        dataIndex: 'costPer1MTokens',
        key: 'costPer1MTokens',
        sorter: (a, b) => (a?.costPer1MTokens ?? 0) - (b?.costPer1MTokens ?? 0),
        render: (value) =>
          renderTag(value, colorMaps.costPer1M, `$${value?.toFixed(4)}`),
      });
    }

    cols.push({
      title: estimatedCostColumnTitle,
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      sorter: (a, b) =>
        (a?.estimatedCost?.value || 0) -
        (b?.estimatedCost?.value || 0),
      render: (cost) =>
        renderTag(cost?.value ?? null, colorMaps.costEstimated, cost?.formatted),
    });

    return cols;
  }, [colorMaps, showDerivedColumns, isClassicTask, estimatedCostColumnTitle]);

  return (
    <div style={{ marginTop: 8 }}>
      <div
        className="fs-unmask"
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Text variant="h4-bold" style={{ margin: 0 }}>
          Infrastructure Comparison
        </Text>
      </div>
      <div className="fs-unmask">
        <DataTable
          rowKey="key"
          dataSource={rowsWithDerived}
          columns={columns}
          pagination={null}
          loading={loading}
        />
      </div>
    </div>
  );
}
